/**
 * RealTimeAnalytics - Comprehensive real-time analytics dashboard
 * Features: Live order tracking, revenue trends, customer insights,
 * interactive charts, and predictive analytics
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  TrendingUp, TrendingDown, DollarSign, ShoppingBag, Users, Coffee,
  Clock, Calendar, RefreshCw, ArrowUpRight, ArrowDownRight, Activity,
  BarChart3, PieChart, Target, Zap, Star, Award, Eye, Percent,
  ChevronRight, AlertCircle, CheckCircle, Package, Timer
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import api from '../../../utils/apiClient';

export default function RealTimeAnalytics() {
  const [dateRange, setDateRange] = useState('7');
  const [loading, setLoading] = useState(true);
  const [realtimeData, setRealtimeData] = useState(null);
  const [ordersFeed, setOrdersFeed] = useState([]);
  const [orderTypeData, setOrderTypeData] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const intervalRef = useRef(null);
  
  // Fetch comprehensive analytics
  const fetchAnalytics = useCallback(async () => {
    try {
      const days = parseInt(dateRange);
      const [ordersRes, productsRes, timeRes, segmentsRes, orderTypesRes] = await Promise.all([
        api.get('/admin/orders?limit=100'),
        api.get(`/admin/analytics/product-performance?days=${days}`),
        api.get(`/admin/analytics/time-insights?days=${days}`),
        api.get(`/admin/analytics/customer-segments?days=${days}`),
        api.get(`/admin/analytics/order-types?days=${days}`)
      ]);
      
      const orders = ordersRes.data || [];
      const products = productsRes.data || {};
      const orderTypes = orderTypesRes.data || null;
      setOrderTypeData(orderTypes);
      const timeInsights = timeRes.data || {};
      const segments = segmentsRes.data || {};
      
      // Process orders for metrics
      const now = new Date();
      const daysAgo = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
      const prevPeriodStart = new Date(daysAgo.getTime() - days * 24 * 60 * 60 * 1000);
      
      const currentOrders = orders.filter(o => new Date(o.created_at) >= daysAgo);
      const prevOrders = orders.filter(o => {
        const date = new Date(o.created_at);
        return date >= prevPeriodStart && date < daysAgo;
      });
      
      // Calculate metrics
      const currentRevenue = currentOrders.reduce((sum, o) => sum + (o.total || 0), 0);
      const prevRevenue = prevOrders.reduce((sum, o) => sum + (o.total || 0), 0);
      const revenueChange = prevRevenue > 0 ? ((currentRevenue - prevRevenue) / prevRevenue * 100) : 0;
      
      const avgOrderValue = currentOrders.length > 0 ? currentRevenue / currentOrders.length : 0;
      const prevAvgOrder = prevOrders.length > 0 ? prevRevenue / prevOrders.length : 0;
      const aovChange = prevAvgOrder > 0 ? ((avgOrderValue - prevAvgOrder) / prevAvgOrder * 100) : 0;
      
      // Order status breakdown
      const statusBreakdown = currentOrders.reduce((acc, o) => {
        acc[o.status] = (acc[o.status] || 0) + 1;
        return acc;
      }, {});
      
      // Daily revenue trend
      const dailyRevenue = [];
      for (let i = days - 1; i >= 0; i--) {
        const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
        const dayStr = date.toISOString().split('T')[0];
        const dayOrders = currentOrders.filter(o => o.created_at.startsWith(dayStr));
        dailyRevenue.push({
          date: dayStr,
          label: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
          revenue: dayOrders.reduce((sum, o) => sum + (o.total || 0), 0),
          orders: dayOrders.length
        });
      }
      
      // Top selling items today
      const today = now.toISOString().split('T')[0];
      const todayOrders = currentOrders.filter(o => o.created_at.startsWith(today));
      const itemCounts = {};
      todayOrders.forEach(order => {
        (order.items || []).forEach(item => {
          const key = item.name;
          if (!itemCounts[key]) {
            itemCounts[key] = { name: item.name, count: 0, revenue: 0 };
          }
          itemCounts[key].count += item.quantity || 1;
          itemCounts[key].revenue += (item.price || 0) * (item.quantity || 1);
        });
      });
      const todayTopItems = Object.values(itemCounts).sort((a, b) => b.count - a.count).slice(0, 5);
      
      setRealtimeData({
        currentRevenue,
        prevRevenue,
        revenueChange,
        totalOrders: currentOrders.length,
        prevOrders: prevOrders.length,
        orderChange: prevOrders.length > 0 ? ((currentOrders.length - prevOrders.length) / prevOrders.length * 100) : 0,
        avgOrderValue,
        aovChange,
        statusBreakdown,
        dailyRevenue,
        todayTopItems,
        todayRevenue: dailyRevenue[dailyRevenue.length - 1]?.revenue || 0,
        todayOrders: todayOrders.length,
        products,
        timeInsights,
        segments,
        completionRate: currentOrders.length > 0 
          ? Math.round(currentOrders.filter(o => o.status === 'completed').length / currentOrders.length * 100)
          : 0
      });
      
      // Recent orders feed
      setOrdersFeed(currentOrders.slice(0, 10).map(o => ({
        id: o.id,
        customer: o.customer_name,
        total: o.total,
        status: o.status,
        time: new Date(o.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
        items: o.items?.length || 0
      })));
      
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    }
    setLoading(false);
  }, [dateRange]);

  useEffect(() => {
    fetchAnalytics();
    // Auto-refresh every 30 seconds
    intervalRef.current = setInterval(fetchAnalytics, 30000);
    return () => clearInterval(intervalRef.current);
  }, [fetchAnalytics]);

  const formatCurrency = (v) => `$${(v || 0).toFixed(2)}`;
  const formatPercent = (v) => `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;

  if (loading && !realtimeData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const data = realtimeData || {};
  const maxDailyRevenue = Math.max(...(data.dailyRevenue?.map(d => d.revenue) || [1]));

  return (
    <div className="space-y-6" data-testid="realtime-analytics">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-heading text-foreground flex items-center gap-2">
            <Activity className="w-6 h-6 text-primary" />
            Real-Time Analytics
          </h2>
          <p className="text-muted-foreground text-sm">Live business performance insights</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
            Live updates
          </div>
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[140px]">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Today</SelectItem>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchAnalytics} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          label="Total Revenue"
          value={formatCurrency(data.currentRevenue)}
          change={data.revenueChange}
          icon={DollarSign}
          color="green"
        />
        <MetricCard
          label="Total Orders"
          value={data.totalOrders}
          change={data.orderChange}
          icon={ShoppingBag}
          color="blue"
        />
        <MetricCard
          label="Avg Order Value"
          value={formatCurrency(data.avgOrderValue)}
          change={data.aovChange}
          icon={Target}
          color="purple"
        />
        <MetricCard
          label="Completion Rate"
          value={`${data.completionRate}%`}
          icon={CheckCircle}
          color="amber"
          subtitle="Orders completed"
        />
      </div>

      {/* Today's Snapshot + Live Feed */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Today's Performance */}
        <div className="lg:col-span-2 bg-card rounded-2xl border border-border p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-medium text-foreground flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" />
              Today's Snapshot
            </h3>
            <span className="text-2xl font-bold text-primary">{formatCurrency(data.todayRevenue)}</span>
          </div>
          
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="text-center p-3 bg-muted/50 rounded-xl">
              <p className="text-2xl font-bold text-foreground">{data.todayOrders}</p>
              <p className="text-xs text-muted-foreground">Orders</p>
            </div>
            <div className="text-center p-3 bg-muted/50 rounded-xl">
              <p className="text-2xl font-bold text-foreground">
                {data.todayOrders > 0 ? formatCurrency(data.todayRevenue / data.todayOrders) : '$0'}
              </p>
              <p className="text-xs text-muted-foreground">Avg Order</p>
            </div>
            <div className="text-center p-3 bg-muted/50 rounded-xl">
              <p className="text-2xl font-bold text-foreground">{data.todayTopItems?.length || 0}</p>
              <p className="text-xs text-muted-foreground">Items Sold</p>
            </div>
          </div>

          {/* Top Selling Items Today */}
          {data.todayTopItems?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-3">Top Sellers Today</h4>
              <div className="space-y-2">
                {data.todayTopItems.map((item, i) => (
                  <div key={item.name} className="flex items-center gap-3">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      i === 0 ? 'bg-amber-100 text-amber-700' :
                      i === 1 ? 'bg-gray-100 text-gray-700' :
                      i === 2 ? 'bg-orange-100 text-orange-700' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      {i + 1}
                    </span>
                    <div className="flex-1">
                      <p className="text-sm font-medium truncate">{item.name}</p>
                    </div>
                    <span className="text-sm text-muted-foreground">{item.count} sold</span>
                    <span className="text-sm font-bold text-primary">{formatCurrency(item.revenue)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Live Orders Feed */}
        <div className="bg-card rounded-2xl border border-border p-6">
          <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-green-500" />
            Recent Orders
          </h3>
          <div className="space-y-3 max-h-[280px] overflow-y-auto">
            <AnimatePresence mode="popLayout">
              {ordersFeed.map((order, i) => (
                <motion.div
                  key={order.id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ delay: i * 0.05 }}
                  className="flex items-center justify-between p-2 bg-muted/50 rounded-lg"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{order.customer}</p>
                    <p className="text-xs text-muted-foreground">{order.time} • {order.items} items</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-primary">{formatCurrency(order.total)}</p>
                    <StatusBadge status={order.status} />
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
            {ordersFeed.length === 0 && (
              <p className="text-center text-muted-foreground py-8">No recent orders</p>
            )}
          </div>
        </div>
      </div>

      {/* Revenue Trend Chart */}
      <div className="bg-card rounded-2xl border border-border p-6">
        <h3 className="font-medium text-foreground mb-6 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-primary" />
          Revenue Trend
        </h3>
        <div className="h-48 flex items-end gap-1">
          {data.dailyRevenue?.map((day, i) => {
            const height = maxDailyRevenue > 0 ? (day.revenue / maxDailyRevenue * 100) : 0;
            return (
              <div 
                key={day.date}
                className="flex-1 flex flex-col items-center group cursor-pointer"
                title={`${day.label}: ${formatCurrency(day.revenue)} (${day.orders} orders)`}
              >
                <div className="w-full relative">
                  <motion.div 
                    initial={{ height: 0 }}
                    animate={{ height: `${height}%` }}
                    transition={{ delay: i * 0.05, duration: 0.5 }}
                    className={`w-full rounded-t-lg transition-colors min-h-[4px] ${
                      i === data.dailyRevenue.length - 1 ? 'bg-primary' :
                      day.revenue > maxDailyRevenue * 0.7 ? 'bg-primary/80' :
                      day.revenue > maxDailyRevenue * 0.4 ? 'bg-primary/60' :
                      'bg-primary/40'
                    } group-hover:bg-primary`}
                    style={{ height: `${height}%`, minHeight: day.revenue > 0 ? '8px' : '4px' }}
                  />
                  {/* Tooltip */}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity bg-foreground text-background text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                    {formatCurrency(day.revenue)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex justify-between mt-2 text-xs text-muted-foreground overflow-x-auto">
          {data.dailyRevenue?.map((day, i) => (
            <span key={day.date} className="flex-1 text-center truncate px-0.5">
              {i === 0 || i === data.dailyRevenue.length - 1 || data.dailyRevenue.length <= 7
                ? new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })
                : ''}
            </span>
          ))}
        </div>
      </div>

      {/* Order Status + Peak Hours */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Order Status Breakdown */}
        <div className="bg-card rounded-2xl border border-border p-6">
          <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
            <PieChart className="w-5 h-5 text-purple-500" />
            Order Status Breakdown
          </h3>
          <div className="space-y-3">
            {Object.entries(data.statusBreakdown || {}).map(([status, count]) => {
              const total = Object.values(data.statusBreakdown || {}).reduce((a, b) => a + b, 0);
              const percent = total > 0 ? (count / total * 100) : 0;
              const colors = {
                pending: 'bg-yellow-500',
                confirmed: 'bg-blue-500',
                preparing: 'bg-purple-500',
                ready: 'bg-green-500',
                completed: 'bg-gray-400',
                cancelled: 'bg-red-500'
              };
              return (
                <div key={status} className="flex items-center gap-3">
                  <span className="w-20 text-sm text-muted-foreground capitalize">{status}</span>
                  <div className="flex-1 h-3 bg-muted rounded-full overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: `${percent}%` }}
                      transition={{ duration: 0.5 }}
                      className={`h-full ${colors[status] || 'bg-primary'} rounded-full`}
                    />
                  </div>
                  <span className="w-12 text-right text-sm font-medium">{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Peak Hours */}
        <div className="bg-card rounded-2xl border border-border p-6">
          <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
            <Timer className="w-5 h-5 text-orange-500" />
            Peak Hours
          </h3>
          {data.timeInsights?.insights ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-xl">
                  <p className="text-xs text-green-600 dark:text-green-400">Busiest Day</p>
                  <p className="text-lg font-bold text-green-700 dark:text-green-300">
                    {data.timeInsights.insights.busiest_day || '-'}
                  </p>
                  <p className="text-xs text-green-600 dark:text-green-400">
                    {data.timeInsights.insights.busiest_day_orders} orders
                  </p>
                </div>
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl">
                  <p className="text-xs text-amber-600 dark:text-amber-400">Peak Hour</p>
                  <p className="text-lg font-bold text-amber-700 dark:text-amber-300">
                    {data.timeInsights.insights.peak_hour || '-'}
                  </p>
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    {data.timeInsights.insights.peak_hour_orders} orders
                  </p>
                </div>
              </div>
              {data.timeInsights.insights.rush_hours?.length > 0 && (
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                  <p className="text-xs text-blue-600 dark:text-blue-400 mb-1">Rush Hours</p>
                  <div className="flex flex-wrap gap-2">
                    {data.timeInsights.insights.rush_hours.map(hour => (
                      <span key={hour} className="px-2 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-sm rounded-lg">
                        {hour}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">No data available</p>
          )}
        </div>
      </div>

      {/* Customer Segments Overview */}
      {data.segments?.stats && (
        <div className="bg-card rounded-2xl border border-border p-6">
          <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-500" />
            Customer Segments
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { key: 'vip', label: 'VIP', icon: Star, color: 'amber' },
              { key: 'regular', label: 'Regular', icon: Users, color: 'blue' },
              { key: 'new', label: 'New', icon: Zap, color: 'green' },
              { key: 'at_risk', label: 'At Risk', icon: AlertCircle, color: 'orange' },
              { key: 'lost', label: 'Lost', icon: Eye, color: 'red' }
            ].map(({ key, label, icon: Icon, color }) => {
              const stats = data.segments.stats[key] || { count: 0, total_spent: 0 };
              const colorClasses = {
                amber: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
                blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
                green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
                orange: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
                red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
              };
              return (
                <div key={key} className="text-center p-3 bg-muted/30 rounded-xl">
                  <div className={`w-10 h-10 rounded-xl ${colorClasses[color]} flex items-center justify-center mx-auto mb-2`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <p className="text-2xl font-bold text-foreground">{stats.count}</p>
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className="text-xs font-medium text-primary mt-1">{formatCurrency(stats.total_spent)}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Order Source Breakdown */}
      {orderTypeData?.by_type && (
        <div className="bg-card rounded-2xl border border-border p-6">
          <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-teal-500" />
            Order Sources
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { key: 'dine_in', label: 'Dine In', icon: Coffee, color: 'bg-emerald-500' },
              { key: 'walk_in_takeaway', label: 'Walk-In Takeaway', icon: Package, color: 'bg-blue-500' },
              { key: 'online', label: 'Online', icon: ShoppingBag, color: 'bg-purple-500' },
              { key: 'kiosk', label: 'Kiosk', icon: Target, color: 'bg-amber-500' }
            ].map(({ key, label, icon: Icon, color }) => {
              const stats = orderTypeData.by_type[key] || { total_orders: 0, total_revenue: 0, order_percentage: 0 };
              return (
                <div key={key} className="p-4 bg-muted/30 rounded-xl border border-border">
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`w-8 h-8 rounded-lg ${color} text-white flex items-center justify-center`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <span className="text-sm font-medium text-foreground">{label}</span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-muted-foreground">Orders</span>
                      <span className="text-lg font-bold text-foreground">{stats.total_orders}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-muted-foreground">Revenue</span>
                      <span className="text-sm font-semibold text-primary">{formatCurrency(stats.total_revenue)}</span>
                    </div>
                    <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                      <motion.div 
                        initial={{ width: 0 }}
                        animate={{ width: `${stats.order_percentage}%` }}
                        transition={{ duration: 0.5 }}
                        className={`h-full ${color} rounded-full`}
                      />
                    </div>
                    <p className="text-xs text-center text-muted-foreground">{stats.order_percentage}% of orders</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-4 pt-4 border-t border-border flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Total Orders: <strong className="text-foreground">{orderTypeData.total_orders}</strong></span>
            <span className="text-muted-foreground">Total Revenue: <strong className="text-primary">{formatCurrency(orderTypeData.total_revenue)}</strong></span>
          </div>
        </div>
      )}
    </div>
  );
}

// Metric Card Component
function MetricCard({ label, value, change, icon: Icon, color, subtitle }) {
  const isPositive = change >= 0;
  const colorClasses = {
    green: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
    blue: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    purple: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
    amber: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'
  };
  
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card rounded-2xl border border-border p-4"
    >
      <div className="flex items-start justify-between mb-2">
        <div className={`w-10 h-10 rounded-xl ${colorClasses[color]} flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
        {typeof change === 'number' && (
          <div className={`flex items-center gap-1 text-xs font-medium ${
            isPositive ? 'text-green-600' : 'text-red-600'
          }`}>
            {isPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {Math.abs(change).toFixed(1)}%
          </div>
        )}
      </div>
      <p className="text-2xl font-bold text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground">{subtitle || label}</p>
    </motion.div>
  );
}

// Status Badge Component
function StatusBadge({ status }) {
  const config = {
    pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    confirmed: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    preparing: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    ready: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    completed: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
    cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
  };
  
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded capitalize ${config[status] || config.pending}`}>
      {status}
    </span>
  );
}
